import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Building, CheckCircle, XCircle, Eye, Calendar, Award, Edit, Search, DollarSign, MapPin, Trash2, Settings as SettingsIcon, RefreshCw, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [pendingEvents, setPendingEvents] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [showBusinessDialog, setShowBusinessDialog] = useState(false);
  
  // Edit states
  const [showEditCustomerDialog, setShowEditCustomerDialog] = useState(false);
  const [showEditBusinessDialog, setShowEditBusinessDialog] = useState(false);
  const [editCustomerForm, setEditCustomerForm] = useState({
    full_name: '',
    email: '',
    phone_number: '',
    address: ''
  });
  const [editBusinessForm, setEditBusinessForm] = useState({
    owner_name: '',
    business_name: '',
    business_type: '',
    business_description: '',
    email: '',
    phone_number: '',
    address: ''
  });
  
  // Bulk selection states
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedBusinesses, setSelectedBusinesses] = useState([]);
  const [selectedEvents, setSelectedEvents] = useState([]);
  
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [drawResults, setDrawResults] = useState(null);
  const [loadingDrawResults, setLoadingDrawResults] = useState(false);
  
  // Delete buttons setting (still used in dashboard)
  const [showDeleteButtons, setShowDeleteButtons] = useState(false);
  
  // Search states
  const [customerSearch, setCustomerSearch] = useState('');
  const [businessSearch, setBusinessSearch] = useState('');
  const [eventSearch, setEventSearch] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Password reset states
  const [passwordResetRequests, setPasswordResetRequests] = useState([]);
  const [selectedResetRequest, setSelectedResetRequest] = useState(null);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [resetFilter, setResetFilter] = useState('pending'); // pending, completed, rejected, all


  useEffect(() => {
    fetchUsers();
    fetchPendingEvents();
    fetchDeleteButtonsSetting();
    fetchPasswordResetRequests();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data.customers || []);
      setBusinesses(response.data.businesses || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

 

  const handleApproveCustomer = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/approve/customer/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Customer approved');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to approve customer');
    }
  };

  const handleRejectCustomer = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/reject/customer/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Customer rejected');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to reject customer');
    }
  };

  const fetchPasswordResetRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/password-reset-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Password reset requests fetched:', response.data);
      setPasswordResetRequests(response.data.requests || []);
    } catch (error) {
      console.error('Error fetching password reset requests:', error);
      toast.error('Failed to load password reset requests');
    }
  };

  const handleGeneratePassword = async (requestId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/password-reset/${requestId}/generate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('Password generated response:', response.data);
      
      setGeneratedPassword(response.data.new_password);
      setSelectedResetRequest(passwordResetRequests.find(r => r.request_id === requestId));
      setShowPasswordDialog(true);
      
      // Refresh the list
      await fetchPasswordResetRequests();
      
      toast.success('New password generated successfully');
    } catch (error) {
      console.error('Generate password error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate password');
    }
  };

  const handleRejectReset = async (requestId) => {
    if (!window.confirm('Are you sure you want to reject this password reset request?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/admin/password-reset/${requestId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      fetchPasswordResetRequests();
      toast.success('Password reset request rejected');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Password copied to clipboard!');
  };

  const handleApproveBusiness = async (businessId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/approve/business/${businessId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Business approved');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to approve business');
    }
  };

  const handleRejectBusiness = async (businessId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/reject/business/${businessId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Business rejected');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to reject business');
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        fetchUsers(),
        fetchPendingEvents(),
        fetchDeleteButtonsSetting()
      ]);
      toast.success('Dashboard refreshed successfully!');
    } catch (error) {
      toast.error('Failed to refresh dashboard');
    } finally {
      setIsRefreshing(false);
    }
  };


  const handleEditCustomer = (customer) => {
    setSelectedCustomer(customer);
    setEditCustomerForm({
      full_name: customer.full_name || '',
      email: customer.email || '',
      phone_number: customer.phone_number || '',
      address: customer.address || ''
    });
    setShowEditCustomerDialog(true);
  };

  const handleUpdateCustomer = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/customer/${selectedCustomer.user_id}`,
        editCustomerForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Customer updated successfully');
      setShowEditCustomerDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update customer');
    }
  };

  const handleEditBusiness = (business) => {
    setSelectedBusiness(business);
    setEditBusinessForm({
      owner_name: business.owner_name || '',
      business_name: business.business_name || '',
      business_type: business.business_type || '',
      business_description: business.business_description || '',
      email: business.email || '',
      phone_number: business.phone_number || '',
      address: business.address || ''
    });
    setShowEditBusinessDialog(true);
  };

  const handleUpdateBusiness = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/business/${selectedBusiness.business_id}`,
        editBusinessForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Business updated successfully');
      setShowEditBusinessDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update business');
    }
  };

  // Bulk selection handlers
  const handleSelectCustomer = (userId) => {
    setSelectedCustomers(prev => 
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleSelectAllCustomers = (customerList) => {
    if (selectedCustomers.length === customerList.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(customerList.map(c => c.user_id));
    }
  };

  const handleSelectBusiness = (businessId) => {
    setSelectedBusinesses(prev => 
      prev.includes(businessId) ? prev.filter(id => id !== businessId) : [...prev, businessId]
    );
  };

  const handleSelectAllBusinesses = (businessList) => {
    if (selectedBusinesses.length === businessList.length) {
      setSelectedBusinesses([]);
    } else {
      setSelectedBusinesses(businessList.map(b => b.business_id));
    }
  };

  const handleSelectEvent = (eventId) => {
    setSelectedEvents(prev => 
      prev.includes(eventId) ? prev.filter(id => id !== eventId) : [...prev, eventId]
    );
  };

  const handleSelectAllEvents = () => {
    if (selectedEvents.length === pendingEvents.length) {
      setSelectedEvents([]);
    } else {
      setSelectedEvents(pendingEvents.map(e => e.event_id));
    }
  };

  // Bulk approval handlers
  const handleBulkApproveCustomers = async () => {
    if (selectedCustomers.length === 0) {
      toast.error('No customers selected');
      return;
    }
    if (selectedCustomers.length > 50) {
      toast.error('Maximum 50 items can be approved at once');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/bulk-approve/customers`,
        { ids: selectedCustomers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${response.data.count} customers approved`);
      setSelectedCustomers([]);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk approve customers');
    }
  };

  const handleBulkRejectCustomers = async () => {
    if (selectedCustomers.length === 0) {
      toast.error('No customers selected');
      return;
    }
    if (selectedCustomers.length > 50) {
      toast.error('Maximum 50 items can be rejected at once');
      return;
    }

    if (!window.confirm(`Are you sure you want to reject ${selectedCustomers.length} customers?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/bulk-reject/customers`,
        { ids: selectedCustomers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${response.data.count} customers rejected`);
      setSelectedCustomers([]);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk reject customers');
    }
  };

  const handleBulkApproveBusinesses = async () => {
    if (selectedBusinesses.length === 0) {
      toast.error('No businesses selected');
      return;
    }
    if (selectedBusinesses.length > 50) {
      toast.error('Maximum 50 items can be approved at once');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/bulk-approve/businesses`,
        { ids: selectedBusinesses },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${response.data.count} businesses approved`);
      setSelectedBusinesses([]);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk approve businesses');
    }
  };

  const handleBulkRejectBusinesses = async () => {
    if (selectedBusinesses.length === 0) {
      toast.error('No businesses selected');
      return;
    }
    if (selectedBusinesses.length > 50) {
      toast.error('Maximum 50 items can be rejected at once');
      return;
    }

    if (!window.confirm(`Are you sure you want to reject ${selectedBusinesses.length} businesses?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/bulk-reject/businesses`,
        { ids: selectedBusinesses },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${response.data.count} businesses rejected`);
      setSelectedBusinesses([]);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk reject businesses');
    }
  };

  const handleBulkApproveEvents = async () => {
    if (selectedEvents.length === 0) {
      toast.error('No events selected');
      return;
    }
    if (selectedEvents.length > 50) {
      toast.error('Maximum 50 items can be approved at once');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/bulk-approve/events`,
        { ids: selectedEvents },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${response.data.count} events approved, ${response.data.notifications_sent} notifications sent`);
      setSelectedEvents([]);
      fetchPendingEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk approve events');
    }
  };

  const handleBulkRejectEvents = async () => {
    if (selectedEvents.length === 0) {
      toast.error('No events selected');
      return;
    }
    if (selectedEvents.length > 50) {
      toast.error('Maximum 50 items can be rejected at once');
      return;
    }

    if (!window.confirm(`Are you sure you want to reject ${selectedEvents.length} events?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/bulk-reject/events`,
        { ids: selectedEvents },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${response.data.count} events rejected`);
      setSelectedEvents([]);
      fetchPendingEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk reject events');
    }
  };


  const fetchPendingEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/events/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch pending events:', error);
    }
  };

  const handleApproveEvent = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/approve/event/${eventId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event approved');
      fetchPendingEvents();
    } catch (error) {
      toast.error('Failed to approve event');
    }
  };

  const handleRejectEvent = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/reject/event/${eventId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event rejected');
      fetchPendingEvents();
    } catch (error) {
      toast.error('Failed to reject event');
    }
  };

  const fetchDeleteButtonsSetting = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.hasOwnProperty('show_delete_buttons')) {
        setShowDeleteButtons(response.data.show_delete_buttons);
      }
    } catch (error) {
      console.error('Error fetching delete buttons setting:', error);
    }
  };

  const handleDeleteCustomer = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this customer? This action cannot be undone and will delete all related data (receipts, coupons, memberships).')) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/delete/customer/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Customer deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete customer');
    }
  };

  const handleDeleteBusiness = async (businessId) => {
    if (!window.confirm('Are you sure you want to delete this business? This action cannot be undone and will delete all events and related data.')) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/delete/business/${businessId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Business deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete business');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event? This action cannot be undone and will delete all receipts, coupons, and winners.')) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/delete/event/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event deleted successfully');
      fetchPendingEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    }
  };

  const handleToggleBusinessEventApproval = async (businessId, enabled) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/business/${businessId}/toggle-event-approval?enabled=${enabled}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(enabled ? 'Event approval enabled for this business' : 'Event approval disabled. Pending events auto-approved.');
      fetchUsers();
      fetchPendingEvents();
    } catch (error) {
      toast.error('Failed to update setting');
    }
  };

  const handleToggleReceiptImageUpload = async (businessId, enabled) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/business/${businessId}/receipt-upload?enabled=${enabled}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(enabled ? 'Receipt image upload enabled for this business' : 'Receipt image upload disabled for this business');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update receipt upload setting');
    }
  };

  const handleToggleEmailFeature = async (businessId, enabled) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/business/${businessId}/email-feature?enabled=${enabled}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(enabled ? 'Promotional email feature enabled for this business' : 'Promotional email feature disabled for this business');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update email feature setting');
    }
  };

  const handleToggleCustomerApproval = async (businessId, enabled) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/business/${businessId}/toggle-customer-approval?enabled=${enabled}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(enabled ? 'Customer approval enabled for this business' : 'Customer approval disabled. Pending memberships auto-approved.');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update customer approval setting');
    }
  };

  const handleToggleActiveStatus = async (businessId, active) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/business/${businessId}/toggle-active-status?active=${active}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(active ? 'Business is now active and visible to customers' : 'Business is now inactive and hidden from search');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update active status');
    }
  };

  const handleToggleReceiptDeletion = async (businessId, allow) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/business/${businessId}/receipt-deletion?allow=${allow}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(allow ? 'Receipt deletion enabled for this business' : 'Receipt deletion disabled for this business');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update receipt deletion setting');
    }
  };

  const handleViewCustomer = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/customer/${userId}/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedCustomer(response.data);
      setShowCustomerDialog(true);
    } catch (error) {
      toast.error('Failed to load customer details');
    }
  };

  // Filter functions
  const filterCustomers = (customersList, searchTerm) => {
    if (!searchTerm) return customersList;
    const search = searchTerm.toLowerCase();
    return customersList.filter(customer => 
      customer.full_name?.toLowerCase().includes(search) ||
      customer.email?.toLowerCase().includes(search) ||
      customer.user_id?.toLowerCase().includes(search) ||
      customer.phone_number?.includes(search)
    );
  };

  const filterBusinesses = (businessesList, searchTerm) => {
    if (!searchTerm) return businessesList;
    const search = searchTerm.toLowerCase();
    return businessesList.filter(business =>
      business.business_name?.toLowerCase().includes(search) ||
      business.owner_name?.toLowerCase().includes(search) ||
      business.email?.toLowerCase().includes(search) ||
      business.business_id?.toLowerCase().includes(search) ||
      business.business_type?.toLowerCase().includes(search)
    );
  };

  const filterEvents = (eventsList, searchTerm) => {
    if (!searchTerm) return eventsList;
    const search = searchTerm.toLowerCase();
    return eventsList.filter(event =>
      event.event_name?.toLowerCase().includes(search) ||
      event.business_name?.toLowerCase().includes(search) ||
      event.event_id?.toLowerCase().includes(search) ||
      event.event_description?.toLowerCase().includes(search)
    );
  };

  const handleViewBusiness = async (businessId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/business/${businessId}/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedBusiness(response.data);
      setShowBusinessDialog(true);
    } catch (error) {
      toast.error('Failed to load business details');
    }
  };

  const handleViewEventDetails = async (event) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/events/${event.event_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedEvent(response.data);
      setShowEventDialog(true);
      
      // Fetch draw results if event is completed
      if (response.data.status === 'completed' || response.data.draw_executed_at) {
        setLoadingDrawResults(true);
        try {
          const winnersResponse = await axios.get(`${API}/events/${event.event_id}/winners`);
          setDrawResults(winnersResponse.data);
        } catch (error) {
          console.error('Error fetching draw results:', error);
          setDrawResults(null);
        } finally {
          setLoadingDrawResults(false);
        }
      } else {
        setDrawResults(null);
      }
    } catch (error) {
      console.error('Error fetching event details:', error);
      toast.error('Failed to load event details');
    }
  };

  // Safe filtering with fallback to empty arrays
  const pendingCustomers = (customers || []).filter(c => c.approval_status === 'pending' || c.approval_status === 'rejected');
  const approvedRejectedCustomers = (customers || []).filter(c => c.approval_status === 'approved');
  const pendingBusinesses = (businesses || []).filter(b => b.approval_status === 'pending' || b.approval_status === 'rejected');
  const approvedRejectedBusinesses = (businesses || []).filter(b => b.approval_status === 'approved');

  return (
    <div className="min-h-screen bg-cream pt-4 sm:pt-6 pb-12 px-4 sm:px-0" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto sm:px-4 lg:px-8">
        <h1 className="font-playfair font-bold text-3xl sm:text-4xl text-primary mb-6 sm:mb-8">Admin Console</h1>

        {/* Stats Cards - Responsive Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <Card className="p-4 sm:p-6">
            <p className="font-outfit text-xs sm:text-sm text-gray-600 mb-1">Total Customers</p>
            <p className="font-playfair text-2xl sm:text-3xl font-bold text-primary">{(customers || []).length}</p>
          </Card>
          <Card className="p-4 sm:p-6">
            <p className="font-outfit text-xs sm:text-sm text-gray-600 mb-1">Pending Customers</p>
            <p className="font-playfair text-2xl sm:text-3xl font-bold text-amber-600">{pendingCustomers.length}</p>
          </Card>
          <Card className="p-4 sm:p-6">
            <p className="font-outfit text-xs sm:text-sm text-gray-600 mb-1">Total Businesses</p>
            <p className="font-playfair text-2xl sm:text-3xl font-bold text-primary">{(businesses || []).length}</p>
          </Card>
          <Card className="p-4 sm:p-6">
            <p className="font-outfit text-xs sm:text-sm text-gray-600 mb-1">Pending Businesses</p>
            <p className="font-playfair text-2xl sm:text-3xl font-bold text-amber-600">{pendingBusinesses.length}</p>
          </Card>
        </div>

        <div className="flex justify-between items-center mb-6">
          <h1 className="font-playfair font-bold text-3xl sm:text-4xl text-primary">Admin Dashboard</h1>
          <div className="flex gap-2">
            <Button
              onClick={handleRefresh}
              variant="outline"
              className="gap-2"
              disabled={isRefreshing}
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
            </Button>
            <Button
              onClick={() => navigate('/admin/settings')}
              variant="outline"
              className="gap-2"
            >
              <SettingsIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Settings</span>
            </Button>
            <Button
              onClick={() => navigate('/admin/logs')}
              variant="outline"
              className="gap-2"
            >
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Logs</span>
            </Button>
          </div>
        </div>

        <Tabs defaultValue="customers">
          {/* Responsive TabsList with scroll */}
          <div className="overflow-x-auto mb-4 sm:mb-6">
            <TabsList className="inline-flex min-w-full sm:min-w-0">
              <TabsTrigger value="customers" className="text-xs sm:text-sm whitespace-nowrap">
                Customers ({approvedRejectedCustomers.length})
              </TabsTrigger>
              <TabsTrigger value="pending-customers" className="text-xs sm:text-sm whitespace-nowrap">
                Pending ({pendingCustomers.length})
              </TabsTrigger>
              <TabsTrigger value="businesses" className="text-xs sm:text-sm whitespace-nowrap">
                Businesses ({approvedRejectedBusinesses.length})
              </TabsTrigger>
              <TabsTrigger value="pending-businesses" className="text-xs sm:text-sm whitespace-nowrap">
                Pending ({pendingBusinesses.length})
              </TabsTrigger>
              <TabsTrigger value="events" className="text-xs sm:text-sm whitespace-nowrap">
                Events ({(pendingEvents || []).length})
              </TabsTrigger>
              <TabsTrigger value="password-resets" className="text-xs sm:text-sm whitespace-nowrap">
                Password Resets
                {passwordResetRequests.filter(r => r.status === 'pending').length > 0 && (
                  <Badge variant="destructive" className="ml-1 text-[10px] px-1">
                    {passwordResetRequests.filter(r => r.status === 'pending').length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Customers Tab - Shows only approved customers */}
          <TabsContent value="customers">
            {/* Search Input */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search by name, email, ID, or phone..."
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-3 sm:space-y-4">
              {filterCustomers(approvedRejectedCustomers, customerSearch).length === 0 ? (
                <Card className="p-8 sm:p-12 text-center">
                  <p className="font-outfit text-sm sm:text-base text-gray-600">
                    {customerSearch ? 'No customers found matching your search' : 'No approved or rejected customers'}
                  </p>
                </Card>
              ) : (
                filterCustomers(approvedRejectedCustomers, customerSearch).map((customer) => (
                <Card key={customer.user_id} className="p-4 sm:p-6" data-testid={`customer-${customer.user_id}`}>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                        <Users className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0" />
                        <h3 className="font-playfair font-bold text-lg sm:text-xl text-primary break-words">{customer.full_name}</h3>
                        <Badge className={
                          customer.approval_status === 'approved' ? 'bg-green-100 text-green-800' :
                          customer.approval_status === 'rejected' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }>
                          {customer.approval_status}
                        </Badge>
                      </div>
                      <div className="space-y-1 text-xs sm:text-sm font-outfit text-gray-600">
                        <p className="break-all"><strong>ID:</strong> {customer.user_id}</p>
                        <p className="break-all"><strong>Email:</strong> {customer.email}</p>
                        <p className="break-all"><strong>Phone:</strong> {customer.phone_number}</p>
                        <p><strong>Registered:</strong> {new Date(customer.created_at).toLocaleString()}</p>
                        {customer.approved_by && (
                          <p className="text-green-700 break-words">
                            <strong>Approved by:</strong> {customer.approved_by_type === 'admin' ? 'Admin' : `Business (${customer.approved_by})`}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-row sm:flex-col gap-2 justify-end sm:justify-start">
                      <Button
                        onClick={() => handleEditCustomer(customer)}
                        size="sm"
                        variant="outline"
                        className="bg-blue-50 hover:bg-blue-100 flex-1 sm:flex-initial text-xs sm:text-sm"
                      >
                        <Edit className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                        <span className="hidden sm:inline">Edit</span>
                      </Button>
                      <Button
                        onClick={() => handleViewCustomer(customer.user_id)}
                        size="sm"
                        variant="outline"
                        className="flex-1 sm:flex-initial text-xs sm:text-sm"
                      >
                        <Eye className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                        <span className="hidden sm:inline">View</span>
                      </Button>
                      {showDeleteButtons && (
                        <Button
                          onClick={() => handleDeleteCustomer(customer.user_id)}
                          size="sm"
                          variant="destructive"
                          className="flex-1 sm:flex-initial text-xs sm:text-sm"
                        >
                          <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                          <span className="hidden sm:inline">Delete</span>
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))
              )}
            </div>
          </TabsContent>

          {/* Pending Customers Tab - Shows only pending customers with Approve/Reject actions */}
          <TabsContent value="pending-customers">
            {pendingCustomers.length > 0 && (
              <div className="mb-4 flex flex-col sm:flex-row gap-2 items-start sm:items-center justify-between bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedCustomers.length === pendingCustomers.length && pendingCustomers.length > 0}
                    onChange={() => handleSelectAllCustomers(pendingCustomers)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-outfit">
                    {selectedCustomers.length > 0 ? `${selectedCustomers.length} selected` : 'Select all'}
                  </span>
                </div>
                {selectedCustomers.length > 0 && (
                  <div className="flex gap-2">
                    <Button
                      onClick={handleBulkApproveCustomers}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve ({selectedCustomers.length})
                    </Button>
                    <Button
                      onClick={handleBulkRejectCustomers}
                      size="sm"
                      variant="destructive"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject ({selectedCustomers.length})
                    </Button>
                  </div>
                )}
              </div>
            )}
            <div className="space-y-3 sm:space-y-4">
              {pendingCustomers.length === 0 ? (
                <Card className="p-8 sm:p-12 text-center">
                  <p className="font-outfit text-sm sm:text-base text-gray-600">No pending or rejected customers</p>
                </Card>
              ) : (
                pendingCustomers.map((customer) => (
                  <Card key={customer.user_id} className="p-4 sm:p-6" data-testid={`pending-customer-${customer.user_id}`}>
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-4">
                      <div className="flex gap-3 flex-1 min-w-0">
                        <input
                          type="checkbox"
                          checked={selectedCustomers.includes(customer.user_id)}
                          onChange={() => handleSelectCustomer(customer.user_id)}
                          className="w-4 h-4 mt-1 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                            <Users className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0" />
                            <h3 className="font-playfair font-bold text-lg sm:text-xl text-primary break-words">{customer.full_name}</h3>
                            <Badge className={customer.approval_status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}>
                              {customer.approval_status === 'rejected' ? 'Rejected' : 'Pending'}
                            </Badge>
                          </div>
                          <div className="space-y-1 text-xs sm:text-sm font-outfit text-gray-600">
                            <p className="break-all"><strong>ID:</strong> {customer.user_id}</p>
                            <p className="break-all"><strong>Email:</strong> {customer.email}</p>
                            <p className="break-all"><strong>Phone:</strong> {customer.phone_number}</p>
                            {customer.address && (
                              <p className="break-words"><strong>Address:</strong> {customer.address}</p>
                            )}
                            <p><strong>Registered:</strong> {new Date(customer.created_at).toLocaleString()}</p>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:flex sm:flex-col gap-2">
                        <Button
                          onClick={() => handleEditCustomer(customer)}
                          size="sm"
                          variant="outline"
                          className="bg-blue-50 hover:bg-blue-100 text-xs sm:text-sm"
                        >
                          <Edit className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                          <span className="hidden sm:inline">Edit</span>
                        </Button>
                        <Button
                          onClick={() => handleViewCustomer(customer.user_id)}
                          size="sm"
                          variant="outline"
                          className="text-xs sm:text-sm"
                        >
                          <Eye className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                          <span className="hidden sm:inline">View</span>
                        </Button>
                        <Button
                          onClick={() => handleApproveCustomer(customer.user_id)}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-xs sm:text-sm"
                          data-testid={`approve-customer-${customer.user_id}`}
                        >
                          <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                          <span className="hidden sm:inline">Approve</span>
                          <span className="sm:hidden">✓</span>
                        </Button>
                        {customer.approval_status === 'pending' && (
                          <Button
                            onClick={() => handleRejectCustomer(customer.user_id)}
                            size="sm"
                            variant="destructive"
                            className="text-xs sm:text-sm"
                            data-testid={`reject-customer-${customer.user_id}`}
                          >
                            <XCircle className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                            <span className="hidden sm:inline">Reject</span>
                            <span className="sm:hidden">✗</span>
                          </Button>
                        )}
                        {showDeleteButtons && (
                          <Button
                            onClick={() => handleDeleteCustomer(customer.user_id)}
                            size="sm"
                            variant="destructive"
                            className="text-xs sm:text-sm bg-red-600 hover:bg-red-700"
                          >
                            <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                            <span className="hidden sm:inline">Delete</span>
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="businesses">
            {/* Search Input */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search by name, owner, email, ID, or type..."
                  value={businessSearch}
                  onChange={(e) => setBusinessSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-3 sm:space-y-4">
              {filterBusinesses(approvedRejectedBusinesses, businessSearch).length === 0 ? (
                <Card className="p-8 sm:p-12 text-center">
                  <p className="font-outfit text-sm sm:text-base text-gray-600">
                    {businessSearch ? 'No businesses found matching your search' : 'No approved or rejected businesses'}
                  </p>
                </Card>
              ) : (
                filterBusinesses(approvedRejectedBusinesses, businessSearch).map((business) => (
                  <Card key={business.business_id} className="p-4 sm:p-6" data-testid={`business-${business.business_id}`}>
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                          <Building className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 flex-shrink-0" />
                          <h3 className="font-playfair font-bold text-lg sm:text-xl text-primary break-words">{business.business_name}</h3>
                          <Badge className={
                            business.approval_status === 'approved' ? 'bg-green-100 text-green-800' :
                            'bg-red-100 text-red-800'
                          }>
                            {business.approval_status}
                          </Badge>
                        </div>
                        <div className="space-y-1 text-xs sm:text-sm font-outfit text-gray-600">
                          <p className="break-all"><strong>ID:</strong> {business.business_id}</p>
                          <p className="break-words"><strong>Owner:</strong> {business.owner_name}</p>
                          <p className="break-all"><strong>Email:</strong> {business.email}</p>
                          <p className="break-words"><strong>Type:</strong> {business.business_type}</p>
                          <p><strong>Registered:</strong> {new Date(business.created_at).toLocaleString()}</p>
                          {business.approved_by && (
                            <p className="text-green-700 break-words"><strong>Approved by Admin:</strong> {business.approved_by}</p>
                          )}
                          {business.qr_code_data && (
                            <p className="break-all">
                              <strong>QR Code Link:</strong>{' '}
                              <a 
                                href={`${window.location.origin}/join/business/${business.business_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 underline"
                              >
                                {window.location.origin}/join/business/{business.business_id}
                              </a>
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-row sm:flex-col gap-2 justify-end sm:justify-start">
                        <Button
                          onClick={() => handleEditBusiness(business)}
                          size="sm"
                          variant="outline"
                          className="bg-blue-50 hover:bg-blue-100 flex-1 sm:flex-initial text-xs sm:text-sm"
                        >
                          <Edit className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                          <span className="hidden sm:inline">Edit</span>
                        </Button>
                        <Button
                          onClick={() => handleViewBusiness(business.business_id)}
                          size="sm"
                          variant="outline"
                          className="flex-1 sm:flex-initial text-xs sm:text-sm"
                        >
                          <Eye className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                          <span className="hidden sm:inline">View</span>
                        </Button>
                        {showDeleteButtons && (
                          <Button
                            onClick={() => handleDeleteBusiness(business.business_id)}
                            size="sm"
                            variant="destructive"
                            className="flex-1 sm:flex-initial text-xs sm:text-sm"
                          >
                            <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                            <span className="hidden sm:inline">Delete</span>
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Pending Businesses Tab */}
          <TabsContent value="pending-businesses">
            {pendingBusinesses.length > 0 && (
              <div className="mb-4 flex flex-col sm:flex-row gap-2 items-start sm:items-center justify-between bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedBusinesses.length === pendingBusinesses.length && pendingBusinesses.length > 0}
                    onChange={() => handleSelectAllBusinesses(pendingBusinesses)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-outfit">
                    {selectedBusinesses.length > 0 ? `${selectedBusinesses.length} selected` : 'Select all'}
                  </span>
                </div>
                {selectedBusinesses.length > 0 && (
                  <div className="flex gap-2">
                    <Button
                      onClick={handleBulkApproveBusinesses}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve ({selectedBusinesses.length})
                    </Button>
                    <Button
                      onClick={handleBulkRejectBusinesses}
                      size="sm"
                      variant="destructive"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject ({selectedBusinesses.length})
                    </Button>
                  </div>
                )}
              </div>
            )}
            <div className="space-y-3 sm:space-y-4">
              {pendingBusinesses.length === 0 ? (
                <Card className="p-8 sm:p-12 text-center">
                  <p className="font-outfit text-sm sm:text-base text-gray-600">No pending or rejected businesses</p>
                </Card>
              ) : (
                pendingBusinesses.map((business) => (
                  <Card key={business.business_id} className="p-4 sm:p-6" data-testid={`pending-business-${business.business_id}`}>
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-4">
                      <div className="flex gap-3 flex-1 min-w-0">
                        <input
                          type="checkbox"
                          checked={selectedBusinesses.includes(business.business_id)}
                          onChange={() => handleSelectBusiness(business.business_id)}
                          className="w-4 h-4 mt-1 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                            <Building className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 flex-shrink-0" />
                            <h3 className="font-playfair font-bold text-lg sm:text-xl text-primary break-words">{business.business_name}</h3>
                            <Badge className={business.approval_status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}>
                              {business.approval_status === 'rejected' ? 'Rejected' : 'Pending Approval'}
                            </Badge>
                          </div>
                          <div className="space-y-1 text-xs sm:text-sm font-outfit text-gray-600">
                            <p className="break-all"><strong>ID:</strong> {business.business_id}</p>
                            <p className="break-words"><strong>Owner:</strong> {business.owner_name}</p>
                            <p className="break-all"><strong>Email:</strong> {business.email}</p>
                            <p className="break-words"><strong>Type:</strong> {business.business_type}</p>
                            <p><strong>Registered:</strong> {new Date(business.created_at).toLocaleString()}</p>
                            {business.qr_code_data && (
                              <p className="break-all">
                                <strong>QR Code Link:</strong>{' '}
                                <a 
                                  href={`${window.location.origin}/join/business/${business.business_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 underline"
                                >
                                  {window.location.origin}/join/business/{business.business_id}
                                </a>
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:flex sm:flex-col gap-2">
                        <Button
                          onClick={() => handleEditBusiness(business)}
                          size="sm"
                          variant="outline"
                          className="bg-blue-50 hover:bg-blue-100 text-xs sm:text-sm"
                        >
                          <Edit className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                          <span className="hidden sm:inline">Edit</span>
                        </Button>
                        <Button
                          onClick={() => handleViewBusiness(business.business_id)}
                          size="sm"
                          variant="outline"
                          className="text-xs sm:text-sm"
                        >
                          <Eye className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                          <span className="hidden sm:inline">View</span>
                        </Button>
                        <Button
                          onClick={() => handleApproveBusiness(business.business_id)}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-xs sm:text-sm"
                          data-testid={`approve-business-${business.business_id}`}
                        >
                          <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                          <span className="hidden sm:inline">Approve</span>
                          <span className="sm:hidden">✓</span>
                        </Button>
                        {business.approval_status === 'pending' && (
                          <Button
                            onClick={() => handleRejectBusiness(business.business_id)}
                            size="sm"
                            variant="destructive"
                            className="text-xs sm:text-sm"
                            data-testid={`reject-business-${business.business_id}`}
                          >
                            <XCircle className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                            <span className="hidden sm:inline">Reject</span>
                            <span className="sm:hidden">✗</span>
                          </Button>
                        )}
                        {showDeleteButtons && (
                          <Button
                            onClick={() => handleDeleteBusiness(business.business_id)}
                            size="sm"
                            variant="destructive"
                            className="text-xs sm:text-sm bg-red-600 hover:bg-red-700"
                          >
                            <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                            <span className="hidden sm:inline">Delete</span>
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="events">
            {/* Search Input */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search by event name, business, ID, or description..."
                  value={eventSearch}
                  onChange={(e) => setEventSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {filterEvents(pendingEvents || [], eventSearch).length > 0 && (
              <div className="mb-4 flex flex-col sm:flex-row gap-2 items-start sm:items-center justify-between bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedEvents.length === filterEvents(pendingEvents || [], eventSearch).length && filterEvents(pendingEvents || [], eventSearch).length > 0}
                    onChange={() => handleSelectAllEvents()}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-outfit">
                    {selectedEvents.length > 0 ? `${selectedEvents.length} selected` : 'Select all'}
                  </span>
                </div>
                {selectedEvents.length > 0 && (
                  <div className="flex gap-2">
                    <Button
                      onClick={handleBulkApproveEvents}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve ({selectedEvents.length})
                    </Button>
                    <Button
                      onClick={handleBulkRejectEvents}
                      size="sm"
                      variant="destructive"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject ({selectedEvents.length})
                    </Button>
                  </div>
                )}
              </div>
            )}
            <div className="space-y-3 sm:space-y-4">
              {filterEvents(pendingEvents || [], eventSearch).length === 0 ? (
                <Card className="p-8 sm:p-12 text-center">
                  <p className="font-outfit text-sm sm:text-base text-gray-600">
                    {eventSearch ? 'No events found matching your search' : 'No pending events'}
                  </p>
                </Card>
              ) : (
                filterEvents(pendingEvents || [], eventSearch).map((event) => (
                  <Card key={event.event_id} className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-4">
                      <div className="flex gap-3 flex-1 min-w-0">
                        <input
                          type="checkbox"
                          checked={selectedEvents.includes(event.event_id)}
                          onChange={() => handleSelectEvent(event.event_id)}
                          className="w-4 h-4 mt-1 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                            <h3 className="font-playfair font-bold text-lg sm:text-xl text-primary break-words">{event.event_name}</h3>
                            <Badge className="bg-yellow-100 text-yellow-800 self-start">Pending Approval</Badge>
                          </div>
                          <p className="font-outfit text-xs sm:text-sm text-gray-600 mb-3 line-clamp-2 break-words">{event.event_description}</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-xs sm:text-sm font-outfit">
                            <div className="space-y-1">
                              <p className="text-gray-600 break-words"><strong>Business:</strong> {event.business_name}</p>
                              <p className="text-gray-600 break-all"><strong>Event ID:</strong> {event.event_id}</p>
                              <p className="text-gray-600"><strong>Incentive Points:</strong> {event.coupon_value}</p>
                            </div>
                            <div className="space-y-1">
                              <p className="text-gray-600"><strong>Start Date:</strong> {new Date(event.start_date).toLocaleDateString()}</p>
                              <p className="text-gray-600"><strong>End Date:</strong> {new Date(event.end_date).toLocaleDateString()}</p>
                              <p className="text-gray-600"><strong>Created:</strong> {new Date(event.created_at).toLocaleDateString()}</p>
                            </div>
                          </div>
                          {event.prizes && event.prizes.length > 0 && (
                            <div className="mt-3">
                              <p className="font-semibold text-xs sm:text-sm mb-1">Prizes:</p>
                              {event.prizes.map((prize, idx) => (
                                <p key={idx} className="text-xs sm:text-sm text-gray-600 break-words">• {prize.prize_name}: ${prize.prize_amount}</p>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-row sm:flex-col gap-2 justify-end sm:justify-start">
                        <Button
                          onClick={() => handleApproveEvent(event.event_id)}
                          className="bg-green-600 hover:bg-green-700 flex-1 sm:flex-initial text-xs sm:text-sm"
                        >
                          <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                          <span className="hidden sm:inline">Approve</span>
                          <span className="sm:hidden">✓</span>
                        </Button>
                        <Button
                          onClick={() => handleRejectEvent(event.event_id)}
                          variant="outline"
                          className="flex-1 sm:flex-initial text-xs sm:text-sm"
                        >
                          <XCircle className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                          <span className="hidden sm:inline">Reject</span>
                          <span className="sm:hidden">✗</span>
                        </Button>
                        {showDeleteButtons && (
                          <Button
                            onClick={() => handleDeleteEvent(event.event_id)}
                            variant="destructive"
                            className="flex-1 sm:flex-initial text-xs sm:text-sm bg-red-600 hover:bg-red-700"
                          >
                            <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                            <span className="hidden sm:inline">Delete</span>
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>


          {/* Password Resets Tab */}
          <TabsContent value="password-resets">
            <div className="mb-6">
              <h2 className="font-playfair text-2xl font-bold mb-4">Password Reset Requests</h2>
              
              {/* Filter Buttons */}
              <div className="flex gap-2 mb-4 flex-wrap">
                <Button
                  variant={resetFilter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setResetFilter('all')}
                >
                  All ({passwordResetRequests.length})
                </Button>
                <Button
                  variant={resetFilter === 'pending' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setResetFilter('pending')}
                >
                  Pending ({passwordResetRequests.filter(r => r.status === 'pending').length})
                </Button>
                <Button
                  variant={resetFilter === 'completed' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setResetFilter('completed')}
                >
                  Completed ({passwordResetRequests.filter(r => r.status === 'completed').length})
                </Button>
                <Button
                  variant={resetFilter === 'rejected' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setResetFilter('rejected')}
                >
                  Rejected ({passwordResetRequests.filter(r => r.status === 'rejected').length})
                </Button>
                
                {/* Test Button */}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    console.log('Test button clicked');
                    setGeneratedPassword('TestPassword123!');
                    setSelectedResetRequest({ 
                      full_name: 'Test User', 
                      email: 'test@example.com', 
                      role: 'customer' 
                    });
                    setShowPasswordDialog(true);
                    console.log('Test dialog triggered');
                  }}
                >
                  Test Dialog
                </Button>
              </div>

              {/* Requests List */}
              <div className="space-y-4">
                {passwordResetRequests
                  .filter(req => resetFilter === 'all' || req.status === resetFilter)
                  .map((request) => (
                    <Card key={request.request_id} className="p-4">
                      <div className="flex flex-col sm:flex-row justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-lg">{request.full_name}</h3>
                            <Badge variant={
                              request.status === 'pending' ? 'default' :
                              request.status === 'completed' ? 'secondary' : 'destructive'
                            }>
                              {request.status}
                            </Badge>
                            <Badge variant="outline">{request.role}</Badge>
                          </div>
                          
                          <div className="space-y-1 text-sm text-gray-600">
                            <p><strong>Email:</strong> {request.email}</p>
                            <p><strong>Request ID:</strong> {request.request_id}</p>
                            <p><strong>Requested:</strong> {new Date(request.requested_at).toLocaleString()}</p>
                            {request.reason && (
                              <p><strong>Reason:</strong> {request.reason}</p>
                            )}
                            {request.resolved_at && (
                              <p><strong>Resolved:</strong> {new Date(request.resolved_at).toLocaleString()}</p>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-col gap-2 sm:w-48">
                          {request.status === 'pending' && (
                            <>
                              <Button
                                onClick={() => handleGeneratePassword(request.request_id)}
                                className="w-full"
                                size="sm"
                              >
                                Generate Password
                              </Button>
                              <Button
                                onClick={() => handleRejectReset(request.request_id)}
                                variant="outline"
                                className="w-full"
                                size="sm"
                              >
                                Reject
                              </Button>
                            </>
                          )}
                          {request.status === 'completed' && request.generated_password && (
                            <Button
                              onClick={() => {
                                console.log('View Password clicked:', {
                                  password: request.generated_password,
                                  request: request
                                });
                                setGeneratedPassword(request.generated_password);
                                setSelectedResetRequest(request);
                                setShowPasswordDialog(true);
                                console.log('Dialog state set to true');
                              }}
                              variant="outline"
                              className="w-full"
                              size="sm"
                            >
                              View Password
                            </Button>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}

                {passwordResetRequests.filter(req => resetFilter === 'all' || req.status === resetFilter).length === 0 && (
                  <Card className="p-8 text-center text-gray-500">
                    <p>No {resetFilter === 'all' ? '' : resetFilter} password reset requests</p>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>


        </Tabs>

        {/* Customer Details Dialog */}
        <Dialog open={showCustomerDialog} onOpenChange={setShowCustomerDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-playfair text-2xl">Customer Details</DialogTitle>
            </DialogHeader>
            {selectedCustomer && (
              <div className="space-y-6 font-outfit">
                {/* Customer Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-playfair font-bold text-lg mb-3 text-primary">Customer Information</h3>
                  <div className="grid md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-gray-600"><strong>Name:</strong> {selectedCustomer.customer?.full_name}</p>
                      <p className="text-gray-600"><strong>Email:</strong> {selectedCustomer.customer?.email}</p>
                      <p className="text-gray-600"><strong>Phone:</strong> {selectedCustomer.customer?.phone_number}</p>
                    </div>
                    <div>
                      <p className="text-gray-600"><strong>User ID:</strong> {selectedCustomer.customer?.user_id}</p>
                      <p className="text-gray-600"><strong>Status:</strong> 
                        <Badge className="ml-2">{selectedCustomer.customer?.approval_status}</Badge>
                      </p>
                      <p className="text-gray-600"><strong>Registered:</strong> {new Date(selectedCustomer.customer?.created_at).toLocaleDateString()}</p>
                      {selectedCustomer.customer?.approved_by && (
                        <p className="text-gray-600"><strong>Approved By:</strong> 
                          <span className="ml-2 text-green-700 font-semibold">{selectedCustomer.customer?.approved_by}</span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Joined Businesses */}
                <div>
                  <h3 className="font-playfair font-bold text-lg mb-3 text-primary flex items-center gap-2">
                    <Building className="w-5 h-5" />
                    Joined Businesses ({(selectedCustomer.businesses || []).length})
                  </h3>
                  {(selectedCustomer.businesses || []).length > 0 ? (
                    <div className="space-y-2">
                      {(selectedCustomer.businesses || []).map((business) => (
                        <Card key={business.business_id} className="p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-semibold text-primary">{business.business_name}</p>
                              <p className="text-sm text-gray-600">{business.business_type}</p>
                            </div>
                            <Badge className="bg-green-100 text-green-800">Active</Badge>
                          </div>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No businesses joined yet</p>
                  )}
                </div>

                {/* Joined Events */}
                <div>
                  <h3 className="font-playfair font-bold text-lg mb-3 text-primary flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Joined Events ({(selectedCustomer.events || []).length})
                  </h3>
                  {(selectedCustomer.events || []).length > 0 ? (
                    <div className="space-y-2">
                      {(selectedCustomer.events || []).map((event) => (
                        <Card key={event.event_id} className="p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <p className="font-semibold text-primary">{event.event_name}</p>
                              <p className="text-sm text-gray-600 mb-1">{event.event_description}</p>
                              <p className="text-xs text-gray-500">
                                {new Date(event.start_date).toLocaleDateString()} - {new Date(event.end_date).toLocaleDateString()}
                              </p>
                            </div>
                            <Badge className={
                              event.status === 'active' ? 'bg-green-100 text-green-800' :
                              event.status === 'completed' ? 'bg-gray-100 text-gray-800' :
                              'bg-blue-100 text-blue-800'
                            }>
                              {event.status}
                            </Badge>
                          </div>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No events joined yet</p>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Business Details Dialog */}
        <Dialog open={showBusinessDialog} onOpenChange={setShowBusinessDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-playfair text-2xl">Business Details</DialogTitle>
            </DialogHeader>
            {selectedBusiness && (
              <div className="space-y-6 font-outfit">
                {/* Business Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-playfair font-bold text-lg mb-3 text-primary">Business Information</h3>
                  <div className="grid md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-gray-600"><strong>Business Name:</strong> {selectedBusiness.business?.business_name}</p>
                      <p className="text-gray-600"><strong>Owner:</strong> {selectedBusiness.business?.owner_name}</p>
                      <p className="text-gray-600"><strong>Email:</strong> {selectedBusiness.business?.email}</p>
                      <p className="text-gray-600"><strong>Type:</strong> {selectedBusiness.business?.business_type}</p>
                    </div>
                    <div>
                      <p className="text-gray-600"><strong>Business ID:</strong> {selectedBusiness.business?.business_id}</p>
                      <p className="text-gray-600"><strong>Status:</strong> 
                        <Badge className="ml-2">{selectedBusiness.business?.approval_status}</Badge>
                      </p>
                      <p className="text-gray-600"><strong>Subscribers:</strong> {selectedBusiness.subscriber_count}</p>
                      <p className="text-gray-600"><strong>Registered:</strong> {new Date(selectedBusiness.business?.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="mt-4 space-y-3 border-t pt-3">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="event-approval-toggle" className="text-sm font-semibold">Event Approval Required:</Label>
                      <Switch 
                        id="event-approval-toggle"
                        checked={selectedBusiness.business?.event_approval_required || false}
                        onCheckedChange={(checked) => {
                          handleToggleBusinessEventApproval(selectedBusiness.business?.business_id, checked);
                          setShowBusinessDialog(false);
                        }}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="receipt-upload-toggle" className="text-sm font-semibold">Receipt Image Upload:</Label>
                        <p className="text-xs text-gray-500">Allow customers to upload receipt images when joining events</p>
                      </div>
                      <Switch 
                        id="receipt-upload-toggle"
                        checked={selectedBusiness.business?.show_receipt_image_upload !== false}
                        onCheckedChange={(checked) => {
                          handleToggleReceiptImageUpload(selectedBusiness.business?.business_id, checked);
                          setShowBusinessDialog(false);
                        }}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="email-feature-toggle" className="text-sm font-semibold">Promotional Email Feature:</Label>
                        <p className="text-xs text-gray-500">Allow business to send promotional emails to subscribers</p>
                      </div>
                      <Switch 
                        id="email-feature-toggle"
                        checked={selectedBusiness.business?.show_email_link || false}
                        onCheckedChange={(checked) => {
                          handleToggleEmailFeature(selectedBusiness.business?.business_id, checked);
                          setShowBusinessDialog(false);
                        }}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="customer-approval-toggle" className="text-sm font-semibold">Premium Business:</Label>
                        <p className="text-xs text-gray-500">Can approve customer accounts when approving join requests</p>
                      </div>
                      <Switch 
                        id="customer-approval-toggle"
                        checked={selectedBusiness.business?.customer_approval_required || false}
                        onCheckedChange={(checked) => {
                          handleToggleCustomerApproval(selectedBusiness.business?.business_id, checked);
                          setShowBusinessDialog(false);
                        }}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="active-status-toggle" className="text-sm font-semibold">Inactive:</Label>
                        <p className="text-xs text-gray-500">Hide business and events from customer search</p>
                      </div>
                      <Switch 
                        id="active-status-toggle"
                        checked={!(selectedBusiness.business?.is_active !== false)}
                        onCheckedChange={(checked) => {
                          handleToggleActiveStatus(selectedBusiness.business?.business_id, !checked);
                          setShowBusinessDialog(false);
                        }}
                      />
                    </div>
                    <div className="flex items-center justify-between border-t pt-3">
                      <div>
                        <Label htmlFor="receipt-deletion-toggle" className="text-sm font-semibold">Receipt Files Deletion:</Label>
                        <p className="text-xs text-gray-500">Allow business to manually delete receipt files from their completed events</p>
                      </div>
                      <Switch 
                        id="receipt-deletion-toggle"
                        checked={selectedBusiness.business?.allow_receipt_deletion || false}
                        onCheckedChange={(checked) => {
                          handleToggleReceiptDeletion(selectedBusiness.business?.business_id, checked);
                          setShowBusinessDialog(false);
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Created Events */}
                <div>
                  <h3 className="font-playfair font-bold text-lg mb-3 text-primary flex items-center gap-2">
                    <Award className="w-5 h-5" />
                    Created Events ({(selectedBusiness.events || []).length})
                  </h3>
                  {(selectedBusiness.events || []).length > 0 ? (
                    <div className="space-y-2">
                      {(selectedBusiness.events || []).map((event) => (
                        <Card key={event.event_id} className="p-4">
                          <div className="flex justify-between items-start gap-4">
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-primary break-words">{event.event_name}</p>
                              <p className="text-sm text-gray-600 mb-1 line-clamp-2">{event.event_description}</p>
                              <div className="flex gap-4 text-xs text-gray-500 mt-2 flex-wrap">
                                <p><strong>ID:</strong> {event.event_id}</p>
                                <p><strong>Points:</strong> {event.coupon_value || 0}</p>
                                <p><strong>Period:</strong> {new Date(event.start_date).toLocaleDateString()} - {new Date(event.end_date).toLocaleDateString()}</p>
                              </div>
                            </div>
                            <div className="flex flex-col gap-2 items-end flex-shrink-0">
                              <Badge className={
                                event.status === 'active' ? 'bg-green-100 text-green-800' :
                                event.status === 'completed' ? 'bg-gray-100 text-gray-800' :
                                'bg-blue-100 text-blue-800'
                              }>
                                {event.status}
                              </Badge>
                              <Badge className={
                                event.approval_status === 'approved' ? 'bg-green-100 text-green-800' :
                                event.approval_status === 'rejected' ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                              }>
                                {event.approval_status}
                              </Badge>
                              <Button
                                onClick={() => handleViewEventDetails(event)}
                                size="sm"
                                variant="outline"
                                className="text-xs"
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                View Details
                              </Button>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No events created yet</p>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Customer Dialog */}
        <Dialog open={showEditCustomerDialog} onOpenChange={setShowEditCustomerDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-playfair text-2xl">Edit Customer Information</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateCustomer} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="edit-full-name">Full Name *</Label>
                <Input
                  id="edit-full-name"
                  value={editCustomerForm.full_name}
                  onChange={(e) => setEditCustomerForm({ ...editCustomerForm, full_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-email">Email *</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editCustomerForm.email}
                  onChange={(e) => setEditCustomerForm({ ...editCustomerForm, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-phone">Phone Number</Label>
                <Input
                  id="edit-phone"
                  value={editCustomerForm.phone_number}
                  onChange={(e) => setEditCustomerForm({ ...editCustomerForm, phone_number: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-address">Address</Label>
                <Textarea
                  id="edit-address"
                  value={editCustomerForm.address}
                  onChange={(e) => setEditCustomerForm({ ...editCustomerForm, address: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowEditCustomerDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90">
                  Save Changes
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Business Dialog */}
        <Dialog open={showEditBusinessDialog} onOpenChange={setShowEditBusinessDialog}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-playfair text-2xl">Edit Business Information</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateBusiness} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="edit-owner-name">Owner Name *</Label>
                <Input
                  id="edit-owner-name"
                  value={editBusinessForm.owner_name}
                  onChange={(e) => setEditBusinessForm({ ...editBusinessForm, owner_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-business-name">Business Name *</Label>
                <Input
                  id="edit-business-name"
                  value={editBusinessForm.business_name}
                  onChange={(e) => setEditBusinessForm({ ...editBusinessForm, business_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-business-type">Business Type *</Label>
                <Input
                  id="edit-business-type"
                  value={editBusinessForm.business_type}
                  onChange={(e) => setEditBusinessForm({ ...editBusinessForm, business_type: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-business-description">Business Description</Label>
                <Textarea
                  id="edit-business-description"
                  value={editBusinessForm.business_description}
                  onChange={(e) => setEditBusinessForm({ ...editBusinessForm, business_description: e.target.value })}
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="edit-business-email">Email *</Label>
                <Input
                  id="edit-business-email"
                  type="email"
                  value={editBusinessForm.email}
                  onChange={(e) => setEditBusinessForm({ ...editBusinessForm, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-business-phone">Phone Number</Label>
                <Input
                  id="edit-business-phone"
                  value={editBusinessForm.phone_number}
                  onChange={(e) => setEditBusinessForm({ ...editBusinessForm, phone_number: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-business-address">Address</Label>
                <Textarea
                  id="edit-business-address"
                  value={editBusinessForm.address}
                  onChange={(e) => setEditBusinessForm({ ...editBusinessForm, address: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowEditBusinessDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90">
                  Save Changes
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Event Details Dialog */}
        <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden rounded-3xl">
            <DialogHeader>
              <DialogTitle className="font-playfair text-xl sm:text-2xl text-primary">Event Details</DialogTitle>
            </DialogHeader>
            <div className="max-h-[calc(85vh-80px)] overflow-y-auto pr-2">
            {selectedEvent && (
              <div className="space-y-4 pb-2">
                <div>
                  <h3 className="font-playfair font-bold text-xl text-primary mb-2">{selectedEvent.event_name}</h3>
                  <p className="text-sm text-gray-600 mb-1">By {selectedEvent.business_name}</p>
                  <p className="text-gray-700">{selectedEvent.event_description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4 py-4 border-y">
                  <div>
                    <div className="flex items-center text-gray-600 mb-2">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span className="text-sm font-semibold">Start Date</span>
                    </div>
                    <p className="text-sm">{new Date(selectedEvent.start_date).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <div className="flex items-center text-gray-600 mb-2">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span className="text-sm font-semibold">End Date</span>
                    </div>
                    <p className="text-sm">{new Date(selectedEvent.end_date).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <div className="flex items-center text-gray-600 mb-2">
                      <span className="text-sm font-semibold">Incentive Points</span>
                    </div>
                    <p className="text-sm font-bold text-green-600">{selectedEvent.coupon_value}</p>
                  </div>
                  <div>
                    <div className="flex items-center text-gray-600 mb-2">
                      <span className="text-sm font-semibold">Status</span>
                    </div>
                    <div className="flex gap-2">
                      <Badge className={
                        selectedEvent.status === 'active' ? 'bg-green-100 text-green-800' :
                        selectedEvent.status === 'completed' ? 'bg-gray-100 text-gray-800' :
                        'bg-blue-100 text-blue-800'
                      }>
                        {selectedEvent.status}
                      </Badge>
                      <Badge className={
                        selectedEvent.approval_status === 'approved' ? 'bg-green-100 text-green-800' :
                        selectedEvent.approval_status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }>
                        {selectedEvent.approval_status}
                      </Badge>
                    </div>
                  </div>
                </div>

                {selectedEvent.prizes && selectedEvent.prizes.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-2">Prizes:</h4>
                    <ul className="space-y-2">
                      {selectedEvent.prizes.map((prize, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-sm">
                          <span className="text-amber-600 font-bold mt-0.5">{idx + 1}.</span>
                          <div className="flex-1">
                            <div className="flex flex-wrap items-baseline gap-2">
                              <span className="font-semibold text-gray-800">{prize.prize_name || `Prize ${idx + 1}`}</span>
                              <span className="text-amber-600 font-bold text-base">${prize.prize_amount}</span>
                            </div>
                            {prize.prize_description && (
                              <p className="text-gray-600 text-xs mt-1">{prize.prize_description}</p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Draw Results Section */}
                {(selectedEvent.status === 'completed' || selectedEvent.draw_executed_at) && (
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <Award className="w-5 h-5 text-amber-600" />
                      Draw Results
                    </h4>
                    {loadingDrawResults ? (
                      <p className="text-sm text-gray-500">Loading results...</p>
                    ) : drawResults && drawResults.draw_executed ? (
                      <div className="space-y-3">
                        <div className="text-xs text-gray-500 mb-3">
                          Draw executed: {new Date(drawResults.executed_at).toLocaleString()}
                        </div>
                        {drawResults.winners && drawResults.winners.length > 0 ? (
                          <ul className="space-y-3">
                            {drawResults.winners.map((winner, idx) => (
                              <li key={idx} className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                <div className="flex items-start gap-3">
                                  <span className="text-amber-600 font-bold text-lg">{idx + 1}.</span>
                                  <div className="flex-1 space-y-1">
                                    <div className="font-semibold text-gray-800">
                                      {winner.prize_name || `Prize ${idx + 1}`} - ${winner.prize_amount}
                                    </div>
                                    <div className="text-sm text-gray-700">
                                      <strong>Winner:</strong> {winner.customer_name || 'N/A'}
                                    </div>
                                    <div className="text-xs text-gray-600">
                                      <strong>Email:</strong> {winner.customer_email || 'N/A'}
                                    </div>
                                    {winner.customer_phone && (
                                      <div className="text-xs text-gray-600">
                                        <strong>Phone:</strong> {winner.customer_phone}
                                      </div>
                                    )}
                                    <div className="text-xs text-gray-500">
                                      Entry ID: {winner.coupon_id}
                                    </div>
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-gray-500">No winners found</p>
                        )}
                      </div>
                    ) : (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                        <p className="text-sm text-gray-600">Draw not executed yet</p>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  <Button
                    onClick={() => setShowEventDialog(false)}
                    className="flex-1 bg-primary hover:bg-primary/90"
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Generated Password Dialog - Moved Outside */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Password Generated</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedResetRequest && (
              <>
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">
                    <strong>User:</strong> {selectedResetRequest.full_name}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Email:</strong> {selectedResetRequest.email}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Role:</strong> {selectedResetRequest.role}
                  </p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-yellow-800 mb-2">⚠️ Important</p>
                  <p className="text-sm text-yellow-700">
                    Copy this password now and send it to the user via email. It will only be shown once.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="font-semibold">New Password</Label>
                  <div className="flex gap-2">
                    <Input
                      value={generatedPassword}
                      readOnly
                      className="font-mono text-lg"
                    />
                    <Button
                      onClick={() => copyToClipboard(generatedPassword)}
                      variant="outline"
                    >
                      Copy
                    </Button>
                  </div>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700">
                    The user can now login with this password. They should change it after their first login.
                  </p>
                </div>
              </>
            )}

            <Button
              onClick={() => {
                setShowPasswordDialog(false);
                setGeneratedPassword('');
                setSelectedResetRequest(null);
              }}
              className="w-full"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}